import { type InterpolateFunction, type TranslateCompiler } from '@ngx-translate/core';
import { IntlMessageFormat } from 'intl-messageformat';

export function createIcuCompiler(): TranslateCompiler {
  const compile = (message: string, locale: string): InterpolateFunction => {
    const formatter = new IntlMessageFormat(message, locale, undefined, { ignoreTag: true });

    return (parameters = {}) => {
      const values = Object.fromEntries(
        Object.entries(parameters).map(([key, value]: readonly [string, unknown]) => {
          if (typeof value !== 'string' && typeof value !== 'number') {
            // eslint-disable-next-line functional/no-throw-statements -- Reject invalid interpolation contracts explicitly.
            throw new TypeError(`ICU parameter "${key}" must be a string or number.`);
          }

          return [key, value];
        }),
      );
      const result = formatter.format(values);
      if (typeof result !== 'string') {
        // eslint-disable-next-line functional/no-throw-statements -- Catalogues are plain text, never rich text.
        throw new TypeError('ICU messages must resolve to plain text.');
      }

      return result;
    };
  };

  return {
    compile,
    compileTranslations: (catalogue, locale) =>
      Object.fromEntries(
        Object.entries(catalogue).map(([key, value]) => {
          if (typeof value !== 'string') {
            // eslint-disable-next-line functional/no-throw-statements -- Flat string catalogues are the Weblate contract.
            throw new TypeError(`Translation "${key}" must be a string.`);
          }

          try {
            return [key, compile(value, locale)];
          } catch (error: unknown) {
            // eslint-disable-next-line functional/no-throw-statements -- Preserve the key and locale when reporting malformed ICU.
            throw new Error(`Invalid ICU translation "${key}" for "${locale}".`, { cause: error });
          }
        }),
      ),
  };
}
